import AuthResource from '/@/modules/auth/resources/AuthResource';
import { commits, getters } from '/@/modules/core/app/helpers/store';
import NotificationService from '/@/modules/core/ui/services/NotificationService';
import { BaseService } from '/@/modules/core/app/services/abstracts/BaseService';
import AnalyticsService from '@/integrations/analytics/AnalyticsService';
import CacheHelper from '/@/modules/core/app/helpers/CacheHelper';
import { AUTH_REMEMBER_ME } from '/@/modules/auth/constants/auth.constants';
import HubspotService from '/@/integrations/hubspot/HubspotService';
import { Hubspot } from '/@/integrations/hubspot/hubspot.constants';
import InboxBackgroundService from '/@/modules/inbox/services/InboxBackgroundService';
import { SignupCompletedEventTracking } from '/@/modules/auth/models/segmentevents/SignupCompletedEventTracking';
import { LoginCompletedEventTracking } from '/@/modules/auth/models/segmentevents/LoginCompletedEventTracking';
import { NewRegistrationEventTracking } from '/@/modules/auth/models/segmentevents/NewRegistrationEventTracking';
import { User } from '/@/modules/user/models/User';
import { StandardPlanLoginClickedEventTracking } from '@/modules/auth/models/segmentevents/StandardPlanLoginClickedEventTracking';
import BroadcastService from '/@/integrations/pusher/BroadcastService';
import GoogleTagManagerService from '/@/integrations/googletagmanager/GoogleTagManagerService';
import { HotjarIdentifyDataLayerItemBuilder } from '/@/integrations/googletagmanager/models/HotjarIdentifyDataLayerItemBuilder';
import { LocalStorageKey } from '/@/modules/core/app/constants/app.constants';
import ClientSessionPreferenceService from '/@/modules/core/app/services/ClientSessionPreferenceService';
import LocalStorage from '/@/modules/core/app/helpers/LocalStorage';
import Lang from '/@/modules/core/app/services/Lang';
import EventBus from '/@/modules/core/app/helpers/EventBus';
import AuthUserBackgroundUIService from '@/modules/auth/services/AuthUserBackgroundUIService';
import { UserConsentEvent } from '@/modules/policyconsent/events/UserConsentEvent';
import HubSpotScriptService from '@/integrations/hubspot/HubSpotScriptService';
import AnnouncementListService from '@/modules/announcement/services/AnnouncementListService';
import { AuthUser } from '@/modules/auth/models/AuthUser';
import { SamlRedirect } from '/@/modules/auth/models/SamlRedirect';
import AuthLoginResponse from '@/modules/auth/models/AuthLoginResponse';
import AsyncRequestService from '@/modules/core/api/services/AsyncRequestService';
import NotificationBroadcastBackgroundService from '@/integrations/pusher/NotificationBroadcastBackgroundService';

export class AuthService extends BaseService {
  /**
   * @var {AuthResource}
   */
  resource;

  /**
   * @var {Promise}
   */
  logoutPromise;

  constructor() {
    super();

    this.resource = AuthResource;
  }

  /**
   * @param {SignUpForm} form
   * @return {Promise<AuthUser>}
   */
  async register(form) {
    const authUser = await this.resource.register(form);
    commits.auth.setUser(authUser);
    GoogleTagManagerService.pushToDataLayer(HotjarIdentifyDataLayerItemBuilder.build(authUser));

    AnalyticsService.init(authUser, true);
    SignupCompletedEventTracking.dispatch(authUser);

    await HubspotService.submitForm(Hubspot.SIGN_UP_FORM_ID, [
      { name: 'full_name', value: form.contact.fullName },
      { name: 'email', value: form.email }
    ]);

    return authUser;
  }

  /**
   * @param {LoginForm} form
   * @return {Promise<AuthLoginResponse>}
   */
  async login(form) {
    await this.logoutPromise;
    const loginResponse = await this.resource.login(form);

    if (loginResponse.isSuccess && loginResponse.authUser) {
      await this.completeUserLogin(loginResponse.authUser);
    }

    return loginResponse;
  }

  async loginWithOtpToken(form) {
    await this.logoutPromise;
    const loginResponse = await this.resource.loginWithOtpToken(form);

    if (loginResponse.isSuccess && loginResponse.authUser) {
      await this.completeUserLogin(loginResponse.authUser);
    }

    return loginResponse;
  }

  /**
   * @param {AuthUser} authUser
   */
  sendAnalyticsEvents(authUser) {
    authUser.currentEstate?.isStandardPlan
      ? StandardPlanLoginClickedEventTracking.dispatch()
      : LoginCompletedEventTracking.dispatch(authUser);
  }

  /**
   * @return {Promise<GenericModel>}
   */
  async loginWithGoogle(currentPath) {
    await this.logoutPromise;
    return this.resource.loginWithGoogle(currentPath);
  }

  /**
   * @return {Promise<SamlRedirect>}
   */
  async loginWithSaml(form) {
    await this.logoutPromise;
    return this.resource.loginWithSaml(form);
  }

  /**
   * @return {Promise<GenericModel>}
   */
  async loginWithFacebook() {
    await this.logoutPromise;
    return this.resource.loginWithFacebook();
  }

  /**
   * @param {?function} callback
   * @return {Promise<void>}
   */
  async logout(callback = null) {
    this.logoutPromise = this.resource.logout();
    await this.logoutPromise;

    InboxBackgroundService.reset();
    AuthUserBackgroundUIService.reset();

    callback?.();
    commits.auth.setUser(null);
    commits.notification.unsetUnreadNotificationCount();
    BroadcastService.destroy();
    HubSpotScriptService.removeWidget();
  }

  /**
   * @param {number} userId
   * @param {?number} estateId
   * @param {function} callback
   * @return {Promise<AuthUser>}
   */
  async impersonateUser(userId, estateId, callback) {
    try {
      const userToImpersonate = await this.resource.impersonateUser(userId, estateId);
      callback(userToImpersonate);

      commits.auth.setUser(userToImpersonate);

      AnalyticsService.init(getters.auth.getUser());
      BroadcastService.init(userToImpersonate);
      AsyncRequestService.init();
      InboxBackgroundService.reInitialize(userToImpersonate);
      AuthUserBackgroundUIService.reInitialize(userToImpersonate);
    } catch (e) {
      NotificationService.show(e.data);
    }
  }

  /**
   * @param {function} callback
   */
  async unimpersonateUser(callback) {
    try {
      const impersonator = await this.resource.unimpersonateUser();
      commits.auth.setUser(impersonator);

      await callback(impersonator);

      AnalyticsService.init(getters.auth.getUser());
      BroadcastService.init(impersonator);
      AsyncRequestService.init();
      InboxBackgroundService.reInitialize(impersonator);
      AuthUserBackgroundUIService.reInitialize(impersonator);
      LocalStorage.setItem(LocalStorageKey.USER_LOCALE, impersonator.locale);
      await Lang.resetI18n(impersonator.locale);
    } catch (e) {
      NotificationService.show(e.data);
    }
  }

  /**
   * @param params
   * @return {Promise<AuthUser>}
   */
  async getAuthUser(params = {}) {
    if (!getters.auth.getUser()) {
      await this.initAuthUser(params);
    }

    return getters.auth.getUser();
  }

  /**
   * @param {{}} params
   * @return {Promise<AuthUser>}
   */
  async initAuthUser(params = {}) {
    const authUser = await this.refreshAuthUser(params);
    AnalyticsService.init(authUser);
    AuthUserBackgroundUIService.initialize(authUser);

    if (authUser && !authUser.hasLatestPolicyConsent) {
      EventBus.signal(UserConsentEvent.NEEDS_POLICY_CONSENT_REQUESTED);
    }

    this.loadInAppAnnouncements(authUser);

    return authUser;
  }

  async loadInAppAnnouncements(authUser) {
    if (!authUser?.permission.canAccessInAppAnnouncements) {
      return;
    }

    const announcements = await AnnouncementListService.getRows({ unseenAnnouncements: true });
    commits.announcement.setUnseenAnnouncements(announcements);
  }

  /**
   * @param {object} params
   * @returns {Promise<AuthUser>}
   */
  async refreshAuthUser(params = {}) {
    const authUser = await this.resource.session(params);

    commits.auth.setUser(authUser);
    BroadcastService.init(authUser);
    NotificationBroadcastBackgroundService.initialize();
    AsyncRequestService.init();
    InboxBackgroundService.initialize(authUser);

    if (authUser) {
      await Lang.resetI18n(
        authUser.locale,
        authUser.currentEstate?.jurisdiction.getLanguages(),
        authUser.permission.canToggleDeveloperMode
      );

      GoogleTagManagerService.pushToDataLayer(HotjarIdentifyDataLayerItemBuilder.build(authUser));
    }

    return getters.auth.getUser();
  }

  /**
   * @param {ForgotPasswordForm} form
   * @return {Promise<*>}
   */
  sendResetPasswordLink(form) {
    return this.resource.sendResetPasswordLink(form);
  }

  /**
   * @returns {Promise<string>}
   */
  sendVerifyEmailLink() {
    return this.resource.sendVerifyEmailLink();
  }

  /**
   * @param {ResetPasswordForm} form
   * @return {Promise<*>}
   */
  resetPassword(form) {
    return this.resource.resetPassword(form);
  }

  /**
   * @param {string} email
   * @param {string} token
   * @return {Promise<*>}
   */
  checkResetToken(email, token) {
    return this.resource.checkResetToken(email, token);
  }

  /**
   * @param {ChangePasswordForm} form
   * @return {Promise<*>}
   */
  changePassword(form) {
    return this.resource.changePassword(form);
  }

  /**
   * @param {InvitedUserForm} form
   * @return {Promise<*>}
   */
  authenticateInvitation(form) {
    return this.resource.authenticateInvitation(form);
  }

  /**
   *
   * @param {InvitedUserForm} form
   * @return {Promise<*>}
   * */
  async confirmInvitation(form) {
    const user = await this.resource.confirmInvitation(form);
    NewRegistrationEventTracking.dispatch(User.mapModel(user));

    return user;
  }

  /**
   * @param {?string} email
   */
  setRememberMe(email) {
    CacheHelper.set(AUTH_REMEMBER_ME, email);
  }

  /**
   * @return {?string}
   */
  getRememberMe() {
    return CacheHelper.get(AUTH_REMEMBER_ME);
  }

  /**
   * @return {Promise<[]>}
   * */
  async getPermissionKeys() {
    return this.resource.getPermissionKeys();
  }

  /**
   * @param {AuthUser} authUser
   * @returns {Promise<void>}
   */
  async completeUserLogin(authUser) {
    ClientSessionPreferenceService.clearUnsupportedMobileViewWarningLocalStorage();
    commits.auth.setUser(authUser);
    BroadcastService.init(authUser);
    AsyncRequestService.init();
    InboxBackgroundService.initialize(authUser);

    await Lang.resetI18n(
      authUser?.locale,
      authUser?.currentEstate?.jurisdiction.getLanguages(),
      authUser?.permission.canToggleDeveloperMode
    );

    GoogleTagManagerService.pushToDataLayer(HotjarIdentifyDataLayerItemBuilder.build(authUser));

    AnalyticsService.init(authUser);
    AuthUserBackgroundUIService.initialize(authUser);

    if (authUser && !authUser.hasLatestPolicyConsent) {
      EventBus.signal(UserConsentEvent.NEEDS_POLICY_CONSENT_REQUESTED);
    }

    this.loadInAppAnnouncements(authUser);

    LocalStorage.setItem(LocalStorageKey.USER_LOCALE, authUser.locale);
    this.sendAnalyticsEvents(authUser);
  }
}

export default new AuthService();
